import {
    ArrowPathIcon,
    ChevronDownIcon,
    ChevronUpIcon,
} from "@heroicons/react/24/outline";
import { TouchpointId } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery } from "@tanstack/react-query";
import type { inferProcedureOutput } from "@trpc/server";
import { EmailBodyWithQuoteToggle } from "components/messages/email-body-with-quote-toggle";
import { trpc } from "components/rpc/trpc";
import { trpcErrorType } from "components/rpc/trpc-error-type";
import { InlineBanner } from "components/ui/inline-banner";
import { InteractiveTextLink } from "components/ui/interactive-text-link";
import { RetryableErrorBanner } from "components/ui/retryable-error-banner";
import React from "react";
import { getSanitizedMarkUpFromEmailBody } from "util/email";
import { stableQueryOptionsRpc } from "util/requests";

type TouchpointEmail = inferProcedureOutput<AppRouter["touchpoint"]["getEmail"]>;

interface EmailHeaderLineProps {
    label: string;
    value: string;
}

const EmailHeaderLine: React.FC<EmailHeaderLineProps> = props => (
    <div className="truncate">
        <span className="text-gray-500">{props.label}: </span>
        <span className="text-gray-700">{props.value}</span>
    </div>
);

const EmailCard: React.FC<{ email: TouchpointEmail }> = ({ email }) => (
    <div
        data-testid="activity-full-email"
        className="mt-1.5 rounded-md border border-gray-200 bg-white">
        <div className="border-b border-gray-100 px-3 py-2 text-xs">
            <EmailHeaderLine label="To" value={email.to.join(", ")} />
            {email.cc.length > 0 ? (
                <EmailHeaderLine label="Cc" value={email.cc.join(", ")} />
            ) : null}
        </div>
        <div className="overflow-x-auto px-3 py-2 [&_img]:max-w-full [&_img]:h-auto">
            <EmailBodyWithQuoteToggle
                kind="html"
                html={email.body}
                sanitize={getSanitizedMarkUpFromEmailBody}
            />
        </div>
    </div>
);

interface ExpandedEmailProps {
    email: TouchpointEmail | undefined;
    error: unknown;
    retrying: boolean;
    onRetry: () => void;
}

const ExpandedEmail: React.FC<ExpandedEmailProps> = props => {
    if (props.email) {
        return <EmailCard email={props.email} />;
    }
    return trpcErrorType(props.error) === "UNAUTHORIZED" ? (
        <InlineBanner type="info">You don&apos;t have access to this email.</InlineBanner>
    ) : (
        <RetryableErrorBanner
            message="Failed to load the email."
            onRetry={props.onRetry}
            retrying={props.retrying}
        />
    );
};

const EmailPreview: React.FC<{ preview: string | null }> = ({ preview }) =>
    preview ? (
        <p className="line-clamp-3 text-sm text-gray-900">
            {preview.replace(/\s+/g, " ")}
        </p>
    ) : null;

type EmailToggleState = "collapsed" | "loading" | "expanded";

const emailToggleLabels: Record<EmailToggleState, string> = {
    collapsed: "Show full email",
    loading: "Loading email…",
    expanded: "Hide email",
};

const emailToggleIcons: Record<
    EmailToggleState,
    React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
    collapsed: ChevronDownIcon,
    loading: ArrowPathIcon,
    expanded: ChevronUpIcon,
};

interface EmailToggleProps {
    state: EmailToggleState;
    onToggle: () => void;
}

const EmailToggle: React.FC<EmailToggleProps> = props => {
    const Icon = emailToggleIcons[props.state];
    return (
        <InteractiveTextLink
            className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium"
            aria-expanded={props.state !== "collapsed"}
            aria-busy={props.state === "loading"}
            onClick={props.onToggle}>
            <Icon
                aria-hidden="true"
                className={`h-3.5 w-3.5 ${props.state === "loading" ? "animate-spin" : ""}`}
            />
            {emailToggleLabels[props.state]}
        </InteractiveTextLink>
    );
};

interface ExpandableEmailBodyProps {
    touchpointId: TouchpointId;
    preview: string | null;
}

export const ExpandableEmailBody: React.FC<ExpandableEmailBodyProps> = props => {
    const [expanded, setExpanded] = React.useState(false);
    const trpcClient = trpc.useTRPC();
    const emailQuery = useQuery({
        ...trpcClient.touchpoint.getEmail.queryOptions({
            touchpointId: props.touchpointId,
        }),
        ...stableQueryOptionsRpc(),
        staleTime: 5 * 60 * 1000,
        retry: false,
        enabled: expanded,
    });
    const toggleState: EmailToggleState = !expanded
        ? "collapsed"
        : emailQuery.isPending
          ? "loading"
          : "expanded";
    return (
        <div>
            {toggleState === "expanded" ? (
                <ExpandedEmail
                    email={emailQuery.data}
                    error={emailQuery.error}
                    retrying={emailQuery.isFetching}
                    onRetry={() => void emailQuery.refetch()}
                />
            ) : (
                <EmailPreview preview={props.preview} />
            )}
            <EmailToggle state={toggleState} onToggle={() => setExpanded(e => !e)} />
        </div>
    );
};
